/**
 * LanguageSelector Component
 * Dropdown for selecting languages with EXECUTE BUTTON in footer
 * Renders via PORTAL with DYNAMIC HEIGHT based on content
 */
import React, { useRef, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, Languages, Sparkles, Loader2 } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { getWorkspaceColors } from './styles';
import { LANGUAGES, MAX_LANGUAGES, ACTION_TYPES } from './constants';

const LanguageSelector = ({ 
  isOpen, 
  selectedLanguages, 
  onToggleLanguage, 
  onClose,
  onExecute,
  isProcessing = false,
  anchorRef,
  maxLanguages = MAX_LANGUAGES,
  title = 'Selecione até 3 idiomas',
  actionType = ACTION_TYPES.TRANSLATE,
}) => {
  const { colors, theme } = useTheme();
  const workspaceColors = getWorkspaceColors(colors, theme === 'dark');
  const dropdownRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0, openDirection: 'up' });

  const canExecute = selectedLanguages.length > 0 && !isProcessing;
  const isTranslate = actionType === ACTION_TYPES.TRANSLATE;
  const ActionIcon = isTranslate ? Languages : Sparkles;
  const actionLabel = isTranslate ? 'Traduzir' : 'Gerar';
  const processingLabel = isTranslate ? 'Traduzindo...' : 'Gerando...';

  // Calculate dynamic height based on number of languages
  const itemHeight = 48; // Height per language item
  const headerHeight = 45;
  const footerHeight = 65;
  const listPadding = 8;
  const listHeight = (LANGUAGES.length * itemHeight) + listPadding;
  const totalHeight = headerHeight + listHeight + footerHeight;
  const maxDropdownHeight = Math.min(totalHeight, 400);

  // Calculate position based on anchor element
  useEffect(() => {
    if (!isOpen || !anchorRef?.current) return;

    const updatePosition = () => {
      const anchor = anchorRef.current;
      const rect = anchor.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      
      // Check space above and below
      const spaceAbove = rect.top;
      const spaceBelow = viewportHeight - rect.bottom;
      
      // Determine open direction
      let openDirection = 'up';
      let top;
      
      if (spaceAbove >= maxDropdownHeight || spaceAbove > spaceBelow) {
        // Open upward
        openDirection = 'up';
        top = rect.top - maxDropdownHeight - 8;
        if (top < 10) top = 10;
      } else {
        // Open downward
        openDirection = 'down';
        top = rect.bottom + 8;
      }

      // Horizontal position
      let left = rect.left;
      const dropdownWidth = 280;
      
      if (left + dropdownWidth > viewportWidth - 10) {
        left = rect.right - dropdownWidth;
      }
      if (left < 10) {
        left = 10;
      }

      setPosition({ top, left, openDirection });
    };

    updatePosition();
    
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, anchorRef, maxDropdownHeight]);

  // Close on click outside (only if not processing)
  useEffect(() => {
    if (!isOpen || isProcessing) return;

    const handleClickOutside = (e) => {
      if (dropdownRef.current?.contains(e.target)) return;
      if (anchorRef?.current?.contains(e.target)) return;
      onClose?.();
    };

    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 50);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, anchorRef, isProcessing]);

  // Close on escape (only if not processing)
  useEffect(() => {
    if (!isOpen || isProcessing) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, isProcessing]);

  if (!isOpen) return null;

  const styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 9998,
    },
    dropdown: {
      position: 'fixed',
      top: position.top,
      left: position.left,
      backgroundColor: workspaceColors.bgPrimary,
      borderRadius: 12,
      boxShadow: '0 8px 40px rgba(0,0,0,0.4)',
      borderWidth: 1,
      borderStyle: 'solid',
      borderColor: workspaceColors.borderColor,
      width: 280,
      maxHeight: maxDropdownHeight,
      zIndex: 9999,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    },
    header: {
      padding: '12px 16px',
      fontSize: 12,
      color: workspaceColors.textSecondary,
      fontWeight: 600,
      borderBottom: `1px solid ${workspaceColors.borderColor}`,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexShrink: 0,
    },
    counter: {
      color: workspaceColors.accentPrimary,
      fontWeight: 700,
    },
    listContainer: {
      flex: 1,
      overflowY: 'auto',
      padding: '4px 0',
    },
    option: {
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      padding: '12px 16px',
      cursor: 'pointer',
      transition: 'background-color 0.1s',
    },
    optionDisabled: {
      opacity: 0.4,
      cursor: 'not-allowed',
    },
    optionSelected: {
      backgroundColor: `${workspaceColors.accentPrimary}15`,
    },
    flag: {
      fontSize: 22,
      lineHeight: 1,
    },
    name: {
      flex: 1,
      fontSize: 14,
      color: workspaceColors.textPrimary,
      fontWeight: 500,
    },
    checkbox: {
      width: 22,
      height: 22,
      borderRadius: 6,
      borderWidth: 2,
      borderStyle: 'solid',
      borderColor: workspaceColors.textTertiary,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.15s ease',
    },
    checkboxSelected: {
      backgroundColor: workspaceColors.accentPrimary,
      borderColor: workspaceColors.accentPrimary,
    },
    footer: {
      padding: '12px 16px',
      borderTop: `1px solid ${workspaceColors.borderColor}`,
      backgroundColor: workspaceColors.bgTertiary,
      flexShrink: 0,
    },
    executeBtn: {
      width: '100%',
      padding: '12px 16px',
      borderRadius: 8,
      border: 'none',
      backgroundColor: canExecute ? workspaceColors.accentPrimary : workspaceColors.bgSecondary,
      color: canExecute ? 'white' : workspaceColors.textTertiary,
      cursor: canExecute ? 'pointer' : 'not-allowed',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      fontSize: 14,
      fontWeight: 600,
      transition: 'all 0.15s ease',
      opacity: selectedLanguages.length === 0 ? 0.5 : 1,
    },
    spinner: {
      animation: 'langSelectorSpin 1s linear infinite',
    },
  };

  const handleExecute = () => {
    if (!canExecute) return;
    onExecute?.();
  };

  const dropdownContent = (
    <>
      {/* Invisible overlay to catch outside clicks */}
      {!isProcessing && <div style={styles.overlay} onClick={onClose} />}
      
      {/* Dropdown */}
      <div 
        ref={dropdownRef}
        style={styles.dropdown}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={styles.header}>
          <span>{title}</span>
          <span style={styles.counter}>
            {selectedLanguages.length}/{maxLanguages}
          </span>
        </div>
        
        {/* Language List */}
        <div style={styles.listContainer}>
          {LANGUAGES.map((lang) => {
            const isSelected = selectedLanguages.includes(lang.code);
            const isDisabled = (!isSelected && selectedLanguages.length >= maxLanguages) || isProcessing;
            
            return (
              <div
                key={lang.code}
                style={{
                  ...styles.option,
                  ...(isSelected ? styles.optionSelected : {}),
                  ...(isDisabled ? styles.optionDisabled : {}),
                }}
                onClick={() => !isDisabled && onToggleLanguage(lang.code)}
                onMouseEnter={(e) => {
                  if (!isDisabled && !isSelected) {
                    e.currentTarget.style.backgroundColor = workspaceColors.bgTertiary;
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = isSelected 
                    ? `${workspaceColors.accentPrimary}15`
                    : 'transparent';
                }}
              >
                <span style={styles.flag}>{lang.flag}</span>
                <span style={styles.name}>{lang.name}</span>
                <div 
                  style={{
                    ...styles.checkbox,
                    ...(isSelected ? styles.checkboxSelected : {}),
                  }}
                >
                  {isSelected && <Check size={14} color="white" strokeWidth={3} />}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer with Execute Button */}
        <div style={styles.footer}>
          <button
            style={styles.executeBtn}
            onClick={handleExecute}
            disabled={!canExecute}
          >
            {isProcessing ? (
              <>
                <Loader2 size={18} style={styles.spinner} />
                {processingLabel}
              </>
            ) : (
              <>
                <ActionIcon size={18} />
                {selectedLanguages.length > 0 
                  ? `${actionLabel} para ${selectedLanguages.length} idioma${selectedLanguages.length > 1 ? 's' : ''}`
                  : 'Selecione idiomas'
                }
              </>
            )}
          </button>
        </div>

        <style>{`
          @keyframes langSelectorSpin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </>
  );

  return createPortal(dropdownContent, document.body);
};

export default LanguageSelector;